import { useEffect, useState, useCallback } from "react"
import { useTranslation } from "react-i18next"
import { MenuItem, ListItemText, Chip, Box, Switch, FormControlLabel } from "@mui/material"
import { debounce } from "lodash"

import {
  PerformanceProfileSettings,
  PerformanceProfileSettingsActions,
  PerformanceProfileSettingsYAxis,
  SettingSectionProps,
} from "@/app/report/common/type"
import { colors } from "@/components/colors.ts"
import TextField from "@/components/form/textField/TextField.tsx"
import { useTranslateMetricKey } from "@/hooks/metric/useTranslateKeyHook.ts"
import SettingSectionItem from "@/app/report/components/setting/SettingSectionItem"
import { DEFAULT_ACTION_TYPES } from "@/app/report/common/tacticMapType.ts"
import { ORDINAL_STRINGS } from "@/app/report/common/constants.ts"
import Selector from "@/components/form/selector/Selector.tsx"
import ColorPicker from "@/app/report/components/setting/ColorPicker.tsx"
import IconButton from "@/components/button/IconButton.tsx"
import Checkbox from "@/components/form/checkbox/Checkbox"

const DEFAULT_PERFORMANCE_PROFILE_SETTING_ITEM_Y_AXIS: PerformanceProfileSettingsYAxis[] = [
  {
    dataType: "speed",
    color: colors.blue[700],
    manualScaleActive: false,
    manualScaleValue: null,
  },
  {
    dataType: "accel",
    color: colors.green[700],
    manualScaleActive: false,
    manualScaleValue: null,
  },
  {
    dataType: "hr",
    color: colors.red[700],
    manualScaleActive: false,
    manualScaleValue: null,
  },
]

const DEFAULT_PERFORMANCE_PROFILE_SETTING_ITEM_ACTIONS: PerformanceProfileSettingsActions = {
  actionCode: "R2",
  color: colors.blue[700],
}

const DEFAULT_PERFORMANCE_PROFILE_SETTING_ITEM = {
  title: "Performance Profile",
  color: colors.red[700],
  yAxis: DEFAULT_PERFORMANCE_PROFILE_SETTING_ITEM_Y_AXIS,
  actions: [DEFAULT_PERFORMANCE_PROFILE_SETTING_ITEM_ACTIONS],
}

const Y_AXIS_METRIC_OPTIONS = [
  { value: "speed", label: "speed" },
  { value: "hr", label: "hr" },
  { value: "accel", label: "acceleration" },
]

export default function PerformanceProfileSection({
  settings,
  tempTemplate,
  setTempTemplate,
  sectionIndex,
}: SettingSectionProps<PerformanceProfileSettings> & { sectionIndex: number }) {
  const { t } = useTranslation()
  const translateKey = useTranslateMetricKey()
  const [tempPerformanceProfileItem, setTempPerformanceProfileItem] =
    useState<PerformanceProfileSettings>(DEFAULT_PERFORMANCE_PROFILE_SETTING_ITEM)
  const [manualScaleValues, setManualScaleValues] = useState<{ [key: number]: string }>({})

  const debouncedUpdateManualScale = useCallback(
    debounce((itemIndex: number, value: number | null) => {
      setTempPerformanceProfileItem((prev) => ({
        ...prev,
        yAxis: prev.yAxis.map((axisItem, index) =>
          index === itemIndex ? { ...axisItem, manualScaleValue: value } : axisItem,
        ),
      }))
    }, 200),
    [],
  )

  useEffect(() => {
    setTempPerformanceProfileItem(settings)
  }, [settings])

  useEffect(() => {
    setTempTemplate((prev) => {
      if (!prev) {
        return null
      }
      const updatedContent = [...prev.content]
      updatedContent[sectionIndex] = {
        ...updatedContent[sectionIndex],
        settings: tempPerformanceProfileItem,
      }

      return { ...prev, content: updatedContent }
    })
  }, [tempPerformanceProfileItem, sectionIndex])

  return (
    <>
      <div className="DailySettingPerformanceProfileSectionContainer">
        <SettingSectionItem key={`performance-profile-setting-item-row-0`}>
          <div className="daily-performance-profile-setting-title-row">
            <TextField
              label={t("title")}
              value={tempPerformanceProfileItem.title}
              onChange={(e) => {
                const newValue = e.target.value

                setTempPerformanceProfileItem((prev) => {
                  return { ...prev, title: newValue }
                })
              }}
              error={tempPerformanceProfileItem.title.length > 35}
              showMessage={tempPerformanceProfileItem.title.length > 35}
              message={
                tempPerformanceProfileItem.title.length > 35
                  ? t("rules.max_len", { name: t("title"), max_len: 35 })
                  : ""
              }
            />
            <div className="daily-setting-performance-profile-action-selector">
              <p className="label-text">{t("actionMetric")}</p>
              <Selector
                type="select"
                multiple
                value={tempPerformanceProfileItem.actions.map((action) => action.actionCode)}
                onChange={(event: { target: { value: any } }) => {
                  const value = event.target.value
                  const newValue = typeof value === "string" ? value.split(",") : value

                  if (newValue.length <= 5) {
                    const newActions = newValue.map((actionCode: string) => {
                      return {
                        actionCode,
                        color: "",
                      }
                    })

                    setTempPerformanceProfileItem((prev) => ({
                      ...prev,
                      actions: newActions,
                    }))
                  }
                }}
                displayEmpty
                renderValue={(selected) => {
                  if (selected.length === 0) {
                    return <span className="placeholder-text">{t("select")}</span>
                  }

                  return (
                    <Box className="chips-container">
                      {selected.map((value) => {
                        const action = DEFAULT_ACTION_TYPES.find((a) => a.value === value)

                        return (
                          <Chip
                            key={value}
                            label={action ? translateKey(action.label) : value}
                            size="small"
                          />
                        )
                      })}
                    </Box>
                  )
                }}
                MenuProps={{
                  PaperProps: {
                    className: "performance-profile-dropdown-menu",
                  },
                }}>
                {DEFAULT_ACTION_TYPES.map((action) => {
                  const isSelected = tempPerformanceProfileItem.actions.some(
                    (selectedAction) => selectedAction.actionCode === action.value,
                  )
                  const isDisabled = !isSelected && tempPerformanceProfileItem.actions.length >= 5

                  return (
                    <MenuItem key={action.value} value={action.value} disabled={isDisabled}>
                      <Checkbox checked={isSelected} size="medium" disabled={isDisabled} />
                      <ListItemText
                        primary={translateKey(action.label)}
                        className={isSelected ? "selected-option" : ""}
                      />
                    </MenuItem>
                  )
                })}
              </Selector>
            </div>
          </div>
          <div className="daily-performance-profile-setting-yAxis-row">
            {tempPerformanceProfileItem.yAxis.map((item, itemIndex) => {
              return (
                <div key={itemIndex} className="daily-performance-profile-setting-yAxis-row-item">
                  <p className="daily-performance-profile-setting-yAxis-label">{`${t(ORDINAL_STRINGS[itemIndex])} ${t("yAxis")}`}</p>
                  <div className="daily-performance-profile-setting-yAxis-metric-wrapper">
                    <p className="daily-performance-profile-setting-yAxis-metric-title">
                      {t("metrics")}
                    </p>
                    <Selector
                      type="select"
                      value={item.dataType || ""}
                      renderValue={(selected) => {
                        const metric = Y_AXIS_METRIC_OPTIONS.find((m) => m.value === selected)

                        if (!metric) {
                          return <span className="placeholder-text">{t("select")}</span>
                        }

                        return (
                          <Box className="metric-container">
                            <div className="metric-value">{translateKey(metric.label)}</div>
                          </Box>
                        )
                      }}
                      onChange={(event: { target: { value: any } }) => {
                        const value = event.target.value
                        setTempPerformanceProfileItem((prev) => {
                          return {
                            ...prev,
                            yAxis: prev.yAxis.map((axisItem, index) =>
                              index === itemIndex ? { ...axisItem, dataType: value } : axisItem,
                            ),
                          }
                        })
                      }}
                      placeholder={t("select")}>
                      {Y_AXIS_METRIC_OPTIONS.map((metric) => {
                        const isAlreadySelected = tempPerformanceProfileItem.yAxis.some(
                          (axis, index) => index !== itemIndex && axis.dataType === metric.value,
                        )

                        return (
                          <MenuItem
                            key={metric.value}
                            value={metric.value}
                            disabled={isAlreadySelected}>
                            <ListItemText
                              primary={translateKey(metric.label)}
                              className={`performance-profile-metric-menu-text ${isAlreadySelected ? "disabled" : ""}`}
                            />
                          </MenuItem>
                        )
                      })}
                    </Selector>
                  </div>
                  <div className="daily-performance-profile-setting-yAxis-manual-scale-wrapper">
                    <p className="daily-performance-profile-setting-yAxis-manual-scale-title">
                      {t("manualScale")}
                    </p>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={item.manualScaleActive}
                          onChange={(e) => {
                            setTempPerformanceProfileItem((prev) => ({
                              ...prev,
                              yAxis: prev.yAxis.map((axisItem, index) =>
                                index === itemIndex
                                  ? { ...axisItem, manualScaleActive: e.target.checked }
                                  : axisItem,
                              ),
                            }))
                          }}
                          size="small"
                        />
                      }
                      label=""
                    />
                  </div>
                  <div className="daily-performance-profile-setting-yAxis-max-value-wrapper">
                    <p className="daily-performance-profile-setting-yAxis-max-value-title">
                      {t("maxValue")}
                    </p>
                    <TextField
                      value={
                        manualScaleValues[itemIndex] !== undefined
                          ? manualScaleValues[itemIndex]
                          : item.manualScaleValue || ""
                      }
                      onChange={(e) => {
                        const inputValue = e.target.value

                        if (!/^\d*\.?\d*$/.test(inputValue) && inputValue !== "") {
                          return
                        }

                        setManualScaleValues((prev) => ({
                          ...prev,
                          [itemIndex]: inputValue,
                        }))

                        const numValue = inputValue === "" ? null : Number(inputValue)
                        debouncedUpdateManualScale(itemIndex, numValue)
                      }}
                      disabled={!item.manualScaleActive}
                    />
                  </div>
                  <ColorPicker
                    color={item.color}
                    setColor={(color) => {
                      setTempPerformanceProfileItem((prev) => ({
                        ...prev,
                        yAxis: prev.yAxis.map((axisItem, index) =>
                          index === itemIndex ? { ...axisItem, color } : axisItem,
                        ),
                      }))
                    }}
                  />
                  <div className="daily-performance-profile-setting-yAxis-add-wrapper">
                    <IconButton
                      type="default"
                      icon="subtract"
                      size="md"
                      onClick={() => {
                        setTempPerformanceProfileItem((prev) => ({
                          ...prev,
                          yAxis: prev.yAxis.filter((_, i) => i !== itemIndex),
                        }))
                      }}
                      disabled={itemIndex === 0}
                    />
                    <div className="add-button-placeholder">
                      {itemIndex === tempPerformanceProfileItem.yAxis.length - 1 && (
                        <IconButton
                          type="default"
                          icon="add"
                          size="md"
                          onClick={() => {
                            const selectedDataTypes = tempPerformanceProfileItem.yAxis.map(
                              (axis) => axis.dataType,
                            )
                            const availableMetric = Y_AXIS_METRIC_OPTIONS.find(
                              (metric) => !selectedDataTypes.includes(metric.value),
                            )

                            if (availableMetric) {
                              const defaultAxisConfig =
                                DEFAULT_PERFORMANCE_PROFILE_SETTING_ITEM_Y_AXIS.find(
                                  (axis) => axis.dataType === availableMetric.value,
                                ) || {
                                  dataType: availableMetric.value,
                                  color: colors.blue[700],
                                  manualScaleActive: false,
                                  manualScaleValue: null,
                                }

                              setTempPerformanceProfileItem((prev) => ({
                                ...prev,
                                yAxis: [...prev.yAxis, defaultAxisConfig],
                              }))
                            }
                          }}
                          disabled={itemIndex === 2}
                        />
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </SettingSectionItem>
      </div>
    </>
  )
}
